<script>
    import "../app.css";
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { page } from "$app/stores";
    import { goto } from "$app/navigation";
    import { Capacitor } from "@capacitor/core";
    import { dev } from "$app/environment";
    import { App as CapApp } from "@capacitor/app";
    import { PersistQueryClientProvider } from "@tanstack/svelte-query-persist-client";
    import { queryClient, persister, PERSIST_MAX_AGE } from "$lib/query/queryClient";
    import { drainOutbox } from "$lib/sync/drainer";
    import { initNetworkStatus, onNetworkChange } from "$lib/sync/networkStatus";
    import { pruneSynced } from "$lib/outbox/outboxApi";
    import { prefetchAllEstablishments } from "$lib/query/prefetch";
    import { warmMisPermisos } from "$lib/java/permisos/permisosback";
    import { loadStorageEstablecimiento } from "$lib/java/establecimientos/establecimientostorage";
    import { activeEst } from "$lib/stores/activeEst.svelte";
    import { sincronizarStatusBar } from "$lib/stores/dark.svelte";
    import { rutaDeDeepLink } from "$lib/deeplinks/rutaDeDeepLink";

    let { children } = $props();

    let pre = import.meta.env.VITE_PRE;

    let prevEstId = activeEst.id;
    $effect(() => {
        const currentId = activeEst.id;
        if (currentId !== prevEstId) {
            prevEstId = currentId;
            const routeId = get(page).route.id;
            if (routeId?.includes("[")) {
                goto(pre + "/inicio");
            }
        }
    });
    import PendingSyncBadge from "$lib/components/PendingSyncBadge.svelte";
    import SyncDrawer from "$lib/components/SyncDrawer.svelte";
    import OfflineIntroModal from "$lib/components/OfflineIntroModal.svelte";
    // Herramienta de PRUEBA del offline: se renderiza a sí misma solo en
    // localhost y en el server de test (ver HOSTS_QA adentro del componente).
    import QaOfflinePanel from "$lib/components/QaOfflinePanel.svelte";

    // Triggers de sincronizacion de la outbox (Paso 6 + Paso 7).
    // drainOutbox ya es barato e idempotente: guard isDraining, early-return si
    // no hay red (isOnline()) y si no hay filas pendientes. Por eso podemos
    // dispararlo seguido sin costo. Triggers:
    //   - boot (Paso 6)
    //   - reconexión — vuelve la red (Paso 6). En la app nativa la señal viene de
    //     @capacitor/network; en web, del evento `online`. onNetworkChange unifica
    //     ambos y sólo dispara en la transición offline→online.
    //   - visibilitychange / focus — el usuario vuelve a la app (clave en mobile:
    //     volvés de otra app o de la pantalla bloqueada) (Paso 7)
    //   - periodico cada 60s — reintenta solo mientras la app esta abierta (Paso 7)
    // (Background Sync API del SW —drenar con la pestaña cerrada— se omite: tiene
    //  soporte despareo en browsers y NO aplica en el WebView de Capacitor, donde
    //  el ciclo de vida nativo + estos triggers cubren el caso real. Ver §0.17.)
    const SYNC_INTERVAL_MS = 60_000;
    onMount(() => {
        pruneSynced().catch(() => {}); // limpia el acumulado historico de filas synced
        initNetworkStatus(); // cablea @capacitor/network (nativo) o eventos web
        // Estado inicial de la status bar. El toggle del tema ya la sincroniza al
        // cambiar, pero en el arranque el tema lo fija el script anti-flash de
        // app.html antes de que hidrate nada, asi que hay que alinearla a mano.
        sincronizarStatusBar(localStorage.getItem("oscuro") === "si");
        const drain = () => drainOutbox().catch(() => {});
        // Revalidación oportunista del pre-cacheo multi-establecimiento: al abrir
        // y al reconectar re-bajamos el snapshot de TODOS los establecimientos
        // (clave por la evicción de IndexedDB en iOS). NO en cada focus/intervalo.
        const prefetchAll = () => prefetchAllEstablishments().catch(() => {});
        // Permisos del usuario en el establecimiento activo, para que los gates
        // de UI esten resueltos en cualquier pantalla de entrada (deep-link, F5).
        const warmPerms = () => {
            const est = loadStorageEstablecimiento();
            if (est?.exist && est?.id) warmMisPermisos(est.id).catch(() => {});
        };
        drain(); // boot
        prefetchAll(); // boot
        warmPerms(); // boot

        const onOnline = () => {
            drain();
            prefetchAll();
            warmPerms();
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") drain();
        };
        const unsubNet = onNetworkChange((isOn) => {
            if (isOn) onOnline();
        });
        window.addEventListener("focus", drain);
        document.addEventListener("visibilitychange", onVisible);
        const interval = setInterval(drain, SYNC_INTERVAL_MS);

        // Botón "atrás" físico de Android: navegar en el history de la SPA en vez
        // de cerrar la app; en la raíz, mandar la app a background (preserva el
        // estado offline). Sólo en la app nativa; en web el plugin no dispara.
        let backHandle;
        let urlHandle;
        if (Capacitor.isNativePlatform()) {
            CapApp.addListener("backButton", ({ canGoBack }) => {
                if (canGoBack) history.back();
                else CapApp.minimizeApp();
            }).then((h) => (backHandle = h));

            // Deep links: el sistema abrio la app con una URL del dominio.
            // Hoy el unico caso es la vuelta desde Mercado Pago
            // (/pagos/exito?preapproval_id=...), que antes moria en el navegador
            // con el plan pago y la app sin enterarse.
            //
            // La URL que llega es la publica (https://app.crecientefertil.com.ar/...)
            // y adentro del WebView las rutas son locales, asi que lo unico que
            // se conserva es el camino y el querystring. Se navega en la SPA en
            // vez de cargar la URL: la app ya esta abierta y con su sesion.
            CapApp.addListener("appUrlOpen", ({ url }) => {
                const destino = rutaDeDeepLink(url);
                if (destino) goto(pre + destino);
            }).then((h) => (urlHandle = h));
        }

        // Service worker: SOLO en la web. En la app empaquetada los archivos ya
        // vienen adentro del paquete (Capacitor sirve index.html para cualquier
        // ruta sin extension, asi que tampoco hace falta su fallback de SPA), y
        // su precache tiene un costo caro: al actualizar desde Play o TestFlight
        // seguia sirviendo el HTML viejo cache-first hasta que el sistema matara
        // el proceso. El SW que ya quedo registrado en los telefonos se borra
        // solo la primera vez (ver service-worker.js); NO volver a registrarlo
        // es lo que evita que reviva en bucle. El offline de datos no se toca:
        // vive en Dexie/IndexedDB y nunca dependio del SW.
        if (!dev && !Capacitor.isNativePlatform() && "serviceWorker" in navigator) {
            navigator.serviceWorker.register("/service-worker.js").catch(() => {});
        }

        return () => {
            unsubNet();
            window.removeEventListener("focus", drain);
            document.removeEventListener("visibilitychange", onVisible);
            clearInterval(interval);
            backHandle?.remove();
            urlHandle?.remove();
        };
    });
</script>

<svelte:head>
    <title>Creciente fertil</title>
</svelte:head>
<PersistQueryClientProvider
    client={queryClient}
    persistOptions={{ persister, maxAge: PERSIST_MAX_AGE }}
>
    <div class="bg-cf-bg min-h-screen">
        <!-- Al cambiar de establecimiento activo, re-montamos la pantalla actual
             para que vuelva a leer el establecimiento (sin recargar la página). -->
        {#key activeEst.id}
            {@render children?.()}
        {/key}
    </div>
    <PendingSyncBadge />
    <SyncDrawer />
    <OfflineIntroModal />
    <QaOfflinePanel />
</PersistQueryClientProvider>
